import React, { useEffect, Fragment } from "react"
import { Box, Button, Typography } from "@material-ui/core"
import { useDispatch, useSelector } from "react-redux"
import AddIcon from "@material-ui/icons/Add"
import {
  editableColumns,
  editableCrewMobileColumns,
} from "../../../../../pages/Shoot/pages/Billing/BillingContext"
import { v4 as uuidv4 } from "uuid"
import { getExpensesTypesList } from "../../../../../actions/billing"
import {
  createCrewAdditionalExpense,
  updateCrewAdditionalExpense,
  deleteCrewAdditionalExpense,
} from "../../../../../actions/billing-crew-client"
import PropTypes from "prop-types"
import isUuid from "../../../../../utils/isUuid"
import { _toggleLoginDialog } from "../../../../../actions/authentication"
import { openSnackBar } from "../../../../../actions/snack-bar"
import { toggleClickConfirmationDialog } from "../../../../../actions/billingDialogs"
import ExpenseTable from "../../../Common/ExpenseTable/index"

// columns flip betweeen before crew approves (1) & after crew approves (2)
const additionalEditableColumns = [
  { ...editableColumns[0], dataProperty: "additional_expenses" },
]
// const additionalLockedColumns = [
//   { ...columns[0], dataProperty: "additional_expenses" },
// ]

const additionalEditableMobileColumns = [
  { ...editableCrewMobileColumns[0], dataProperty: "additional_expenses" },
]

const AdditionalItemsTable = ({
  crewDealMemo = {},
  setCrewDealMemo = () => {},
  confirmed = false,
}) => {
  const dispatch = useDispatch()
  const authenticatedUser = useSelector(
    state => state.authentication.authenticatedUser
  )

  const activeCompany = useSelector(state => state.companies.activeCompany)
  const expensesTypesList = useSelector(
    state => state.billing.expensesTypesList
  )

  const crewMember = crewDealMemo && crewDealMemo.user
  const additionalDisabled = crewMember.approved === 1

  const viewportWidth =
    window.innerWidth || document.documentElement.clientWidth

  // load expense types list for use in autocomplete
  useEffect(() => {
    if (expensesTypesList.length === 0) {
      let queryParameterData = {
        queryParameter: true,
        company_id: crewDealMemo.company_id,
      }
      if (activeCompany) {
        dispatch(getExpensesTypesList())
      } else {
        dispatch(getExpensesTypesList(queryParameterData))
      }
    }
  }, [])

  // *** ADD BLANK ITEM ***
  //adds base object into local state - creation / deletion handled in the specified calls below
  const addItem = () => {
    let newItem = {
      additional_expense_id: uuidv4(),
      shoot_id: crewDealMemo.shoot_id,
      shoot_role: crewMember.shoot_role_id,
      user_id: crewMember.user_id,
      type_name: "",
      item_type: null,
      quantity: 0,
      actual_rate: 0,
      client_rate: 0,
      discount: 0,
      description: "",
      is_percentage: 0,
    }
    setCrewDealMemo(oldState => {
      let newAdditional = [...oldState.user.additional_expenses, newItem]
      return {
        ...oldState,
        user: { ...oldState.user, additional_expenses: newAdditional },
      }
    })
  }
  // *** ADD BLANK ITEM END ***

  // *** DELETE ITEM ***
  const deleteItem = item => {
    let expenseId = item.additional_expense_id
    let shootRoleId = item.shoot_role
    if (!isUuid(item.additional_expense_id)) {
      // if already in the db as an item, delete call made
      dispatch(deleteCrewAdditionalExpense(expenseId, shootRoleId))
        .then(() => {
          setCrewDealMemo(oldState => {
            let updatedList = oldState.user.additional_expenses.filter(
              exp => exp.additional_expense_id !== item.additional_expense_id
            )
            return {
              ...oldState,
              user: { ...oldState.user, additional_expenses: updatedList },
            }
          })
          dispatch(openSnackBar({ message: "Expense deleted successfully" }))
        })
        .catch(err => {
          console.log(err)
          dispatch(
            openSnackBar({ message: "Error attempting to delete expenses" })
          )
        })
    } else {
      // if only exists locally (as an added but not created newitem with a uuid) - remove from local state
      setCrewDealMemo(oldState => {
        let updatedList = oldState.user.additional_expenses.filter(
          exp => exp.additional_expense_id !== item.additional_expense_id
        )
        return {
          ...oldState,
          user: { ...oldState.user, additional_expenses: updatedList },
        }
      })
      dispatch(openSnackBar({ message: "Expense deleted successfully" }))
    }
  }

  // open click confrimation dialog & pass needed fn & args
  const openDeleteConfirmation = (item, context) => {
    let data = {
      action: "delete this expense",
      confirmationFunc: deleteItem,
      funcArgs: [item],
    }
    Object.keys(authenticatedUser).length > 0
      ? dispatch(toggleClickConfirmationDialog(true, data))
      : dispatch(_toggleLoginDialog(true))
  }
  // *** DELETE ITEM END ***

  //handles both create & update
  const saveAdditionalExpense = (values, actions, item) => {
    if (Object.keys(authenticatedUser).length === 0) {
      actions.setSubmitting(false)
      dispatch(_toggleLoginDialog(true))
    } else {
      actions.setSubmitting(true)
      let newExpenseData = {
        shoot_id: item.shoot_id,
        user_id: item.user_id,
        shoot_role: item.shoot_role,
        status_id: 3, //defaults to accepted status id
        item_type: values.item_type,
        quantity: values.quantity,
        actual_rate: values.actual_rate,
        client_rate: values.client_rate || 0,
        discount: values.discount || 0,
        description: values.description,
        is_percentage: values.is_percentage || 0,
        created_by: "crew",
      }
      let requestData = {
        data: [newExpenseData],
      }
      if (isUuid(item.additional_expense_id)) {
        // CREATE A NEW ADDITIONAL ITEM
        dispatch(createCrewAdditionalExpense(requestData))
          .then(res => {
            let createdExpense = {
              ...res.data[0],
              id: newExpenseData.shoot_role,
              additional_expense_id: res.data[0].id,
            }
            setCrewDealMemo(oldState => {
              // find the one with the matching uuid, spread new data into it
              let updatedAdditionalExpensesArr = oldState.user.additional_expenses.map(
                x => {
                  if (item.additional_expense_id === x.additional_expense_id) {
                    return { ...x, ...createdExpense }
                  } else {
                    return x
                  }
                }
              )
              return {
                ...oldState,
                user: {
                  ...oldState.user,
                  additional_expenses: updatedAdditionalExpensesArr,
                },
              }
            })
            actions.setSubmitting(false)
            actions.resetForm({ values: values })
            dispatch(openSnackBar({ message: "Successfully added expense" }))
          })
          .catch(err => {
            console.log(err)
            actions.setSubmitting(false)
            actions.resetForm({ values: values })
            dispatch(openSnackBar({ message: "Error adding new expense" }))
          })
      } else {
        // UPDATE EXISTING ADDITIONAL ITEM
        dispatch(
          updateCrewAdditionalExpense(
            newExpenseData,
            item.additional_expense_id
          )
        )
          .then(res => {
            setCrewDealMemo(oldState => {
              let updated = {
                additional_expense_id: res.updated_data.id,
                actual_rate: res.updated_data.actual_rate,
                client_rate: res.updated_data.actual_rate,
                description: res.updated_data.description,
                item_type: res.updated_data.item_type,
                quantity: res.updated_data.quantity,
                status_id: 3, //defaults to accepted status id
                created_by: "crew",
              }
              let newAdditional = oldState.user.additional_expenses.map(x => {
                if (x.additional_expense_id === res.updated_data.id) {
                  return { ...x, ...updated }
                } else {
                  return x
                }
              })
              return {
                ...oldState,
                user: { ...oldState.user, additional_expenses: newAdditional },
              }
            })
            actions.setSubmitting(false)
            actions.resetForm({ values: values })
            dispatch(openSnackBar({ message: "Updated expense" }))
          })
          .catch(err => {
            console.log(err)
            actions.setSubmitting(false)
            actions.resetForm({ values: values })
            dispatch(openSnackBar({ message: "Error updating expense" }))
          })
      }
    }
  }

  return (
    <Fragment>
      {viewportWidth > 600 ? (
        <Typography align="left" variant="h2">
          Additional Items
        </Typography>
      ) : null}
      <Box mt={2}>
        <ExpenseTable
          columns={
            viewportWidth > 600
              ? additionalEditableColumns
              : additionalEditableMobileColumns
          }
          data={crewMember}
        >
          <ExpenseTable.Header />
          <ExpenseTable.ItemsGroup>
            <ExpenseTable.ItemBar onSave={saveAdditionalExpense}>
              <ExpenseTable.NameItem
                additionalDisabled={additionalDisabled}
                style={
                  viewportWidth < 600
                    ? {
                        gridColumn: "1/ span 6",
                      }
                    : {}
                }
              />
              {viewportWidth > 600 ? (
                <ExpenseTable.NumberItem
                  valueForProperty="quantity"
                  additionalDisabled={additionalDisabled}
                />
              ) : (
                <Box>
                  <Typography
                    style={{
                      marginTop: "5px",
                      fontSize: "12px",
                      color: "#9A9A9A",
                      textAlign: "center",
                    }}
                  >
                    Qty
                  </Typography>
                  <ExpenseTable.NumberItem
                    valueForProperty="quantity"
                    additionalDisabled={additionalDisabled}
                  />
                </Box>
              )}
              {viewportWidth > 600 ? (
                <ExpenseTable.NumberItem
                  valueForProperty="actual_rate"
                  additionalDisabled={additionalDisabled}
                />
              ) : (
                <Box>
                  <Typography
                    style={{
                      marginTop: "5px",
                      fontSize: "12px",
                      color: "#9A9A9A",
                      textAlign: "center",
                    }}
                  >
                    Rate($)
                  </Typography>
                  <ExpenseTable.NumberItem
                    valueForProperty="actual_rate"
                    additionalDisabled={additionalDisabled}
                  />
                </Box>
              )}
              {viewportWidth > 600 ? (
                <ExpenseTable.TotalItem additionalDisabled={additionalDisabled}>
                  {values => (values.actual_rate * values.quantity).toFixed(2)}
                </ExpenseTable.TotalItem>
              ) : (
                <Box>
                  <Typography
                    style={{
                      marginTop: "5px",
                      marginBottom: "5px",
                      fontSize: "12px",
                      color: "#9A9A9A",
                      textAlign: "center",
                    }}
                  >
                    Total($)
                  </Typography>
                  <ExpenseTable.TotalItem
                    justifyContent="center"
                    additionalDisabled={additionalDisabled}
                  >
                    {values =>
                      (values.actual_rate * values.quantity).toFixed(2)
                    }
                  </ExpenseTable.TotalItem>
                </Box>
              )}
              <ExpenseTable.Item
                additionalDisabled={additionalDisabled}
                valueForProperty="description"
                style={
                  viewportWidth > 600
                    ? {
                        gridColumn: "type_name-start / quantity-start",
                        gridRow: "2/2",
                      }
                    : {
                        gridRow: "2/3",
                        gridColumn: "1/ span 6",
                      }
                }
              />
              <Box
                style={
                  viewportWidth > 600
                    ? {
                        gridColumn: "quantity-start / total-end",
                        gridRow: "2/2",
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        padding: "0 5px",
                      }
                    : {
                        gridColumn: "quantity-start / total-end",
                        gridRow: "5/5",
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        padding: "0 5px",
                        marginBottom: "10px",
                      }
                }
              >
                <ExpenseTable.DeleteButton onClick={openDeleteConfirmation} />
                <ExpenseTable.SaveButton />
              </Box>
            </ExpenseTable.ItemBar>
          </ExpenseTable.ItemsGroup>
        </ExpenseTable>
        {/* Only showing the add item button when has not been confirmed */}
        {!confirmed && (
          <Box style={viewportWidth > 600 ? { marginLeft: 30 } : {}}>
            <Button
              onClick={() =>
                Object.keys(authenticatedUser).length > 0
                  ? addItem()
                  : dispatch(_toggleLoginDialog(true))
              }
              color="secondary"
              style={
                viewportWidth > 600
                  ? { fontSize: "18px" }
                  : { fontSize: "14px" }
              }
            >
              <AddIcon
                style={
                  viewportWidth > 600
                    ? { fontSize: "18px" }
                    : { fontSize: "14px" }
                }
              />
              Add Item
            </Button>
          </Box>
        )}
      </Box>
    </Fragment>
  )
}

AdditionalItemsTable.propTypes = {
  crewDealMemo: PropTypes.object,
  setCrewDealMemo: PropTypes.func,
  confirmed: PropTypes.bool,
}

export default AdditionalItemsTable
